import type { AdaptivePlayerProfile } from '@texas42/app-model';
import type {
  AIActionDecisionEvidence,
  FilteredGameState,
  SerializableAIConfig,
  ValidAction,
} from '@texas42/game-runtime';
import type { PlayAdviceStatus } from './play-advice.js';

export interface DecisionDiagnostics {
  elapsedMs: number;
  deadlineExceeded?: boolean;
  worldsEvaluated?: number;
  cancelled?: boolean;
  /** Phase-specific AI policy version used for a persisted decision receipt. */
  policyVersion?: number;
  /** Deterministic seed derived for this decision. */
  decisionSeed?: number;
  strategy: string;
  profileId?: string;
  fallback?: boolean;
  candidateCount: number;
  evidence?: AIActionDecisionEvidence;
}

export type AIWorkerRequest =
  | {
      type: 'DECIDE';
      requestId: string;
      stateRevision: number;
      playerId: number;
      viewState: FilteredGameState;
      validActions: ValidAction[];
      config: SerializableAIConfig;
      adaptiveProfile?: AdaptivePlayerProfile;
      deadlineMs: number;
    }
  | {
      type: 'ADVISE';
      requestId: string;
      stateRevision: number;
      playerId: number;
      viewState: FilteredGameState;
      validActions: ValidAction[];
      config: SerializableAIConfig;
      deadlineMs: number;
    }
  | { type: 'CANCEL'; requestId: string };

export type AIWorkerResponse =
  | {
      type: 'DECISION';
      requestId: string;
      stateRevision: number;
      transitionId: string;
      diagnostics?: DecisionDiagnostics;
    }
  | {
      type: 'ADVICE';
      requestId: string;
      stateRevision: number;
      transitionId: string;
      status: Extract<PlayAdviceStatus, 'recommended' | 'near-equal' | 'forced'>;
      candidateCount: number;
      diagnostics?: DecisionDiagnostics;
    }
  | {
      type: 'ERROR';
      requestId: string;
      stateRevision: number;
      message: string;
    };

export const DEFAULT_AI_DEADLINE_MS = 4_000;
